import { productEvent } from "@shared/analytics/productEvents";
import { linkVariants } from "@shared/components/ui/link";
import { formatNumber } from "@solomon-apps/shared";
import { BigDecimal } from "@solomon-labs/bigdecimal";
import { DataTable } from "@solomon-labs/design/components/data";
import type { DataTableColumn } from "@solomon-labs/design/components/data";
import {
    ArrowShortUpRightIcon,
    ChevronDoubleLeftIcon,
    ChevronDoubleRightIcon
} from "@solomon-labs/design/components/icons";
import { MetricValue } from "@solomon-labs/design/components/metrics";
import {
    ButtonIcon,
    Copy,
    Text,
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger
} from "@solomon-labs/design/components/ui";
import React from "react";

import { usePublicDistributions } from "@/hooks/stats/usePublicDistributions";
import type { DistributionAsset } from "@/hooks/stats/usePublicDistributions";

import { DistributionHistoryEmpty } from "./DistributionHistoryEmpty";
import { DistributionHistoryError } from "./DistributionHistoryError";

interface DistributionRow {
    id: string;
    dateStr: string;
    sigFull: string | null;
    sigShort: string;
    transactionUrl: string;
    amountStr: string;
    apyStr: string;
}

function formatAmount(amount: string, asset: DistributionAsset): string {
    return `${formatNumber(amount, { maximumFractionDigits: asset.decimals })} ${asset.symbol}`;
}

/** Explain the annualized return for one distribution period. */
function ExplainedHeader({
    label,
    explanation
}: {
    label: string;
    explanation: string;
}): React.ReactElement {
    return (
        <TooltipProvider delay={300}>
            <Tooltip>
                <TooltipTrigger
                    render={<span className="cursor-help">{label}</span>}
                />
                <TooltipContent side="top">{explanation}</TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}

const COLUMNS: DataTableColumn<DistributionRow>[] = [
    {
        header: "Date",
        cell: (row) =>
            !row.sigFull ? (
                row.dateStr
            ) : (
                <Text
                    variant="small"
                    render={
                        <a
                            href={row.transactionUrl}
                            onClick={() =>
                                productEvent("stats:link_clicked", {
                                    entry_point: "stats_distributions",
                                    link_type: "transaction_explorer"
                                })
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            className={linkVariants()}
                        />
                    }
                >
                    {row.dateStr}
                </Text>
            ),
        align: "left",
        numeric: false
    },
    {
        header: "Transaction",
        // The displayed signature is truncated, so following the link is the
        // only way to get the full string off this page. `Copy` puts the
        // untruncated signature on the clipboard without widening the column.
        cell: (row) =>
            !row.sigFull ? (
                "—"
            ) : (
                <Copy
                    content={row.sigFull}
                    copyLabel="Copy signature"
                    copiedLabel="Signature copied"
                >
                    <Text
                        variant="small"
                        render={
                            <a
                                href={row.transactionUrl}
                                onClick={() =>
                                    productEvent("stats:link_clicked", {
                                        entry_point: "stats_distributions",
                                        link_type: "transaction_explorer"
                                    })
                                }
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`${linkVariants()} inline-flex items-center gap-1`}
                            />
                        }
                    >
                        {row.sigShort}
                        <ArrowShortUpRightIcon className="size-4" />
                    </Text>
                </Copy>
            ),
        align: "left",
        numeric: false
    },
    {
        header: (
            <ExplainedHeader
                label="APY"
                explanation="Annualized return for this distribution period across all recipients"
            />
        ),
        cell: (row) => row.apyStr,
        align: "right"
    },
    {
        header: "Amount",
        cell: (row) => row.amountStr,
        align: "right"
    }
];

const PAGE_SIZE = 20;

/**
 * The distribution record: three summary figures and the history table.
 *
 * Shared by `/stats/distributions` and the Yield tab, which used to show a
 * teaser card linking to the page. One component so the two cannot drift.
 *
 * Paged at 20. `data-table.intent.md` is explicit that pagination state
 * belongs above the table. YaaS returns the requested page and full totals.
 * Product rules: Codex-docs/intents/public-rewards.md.
 */
export function DistributionHistory(): React.ReactElement {
    const [page, setPage] = React.useState(0);
    const query = usePublicDistributions(page);
    const data = query.data;
    const isLoading = query.isLoading;
    const isError = query.isError || (!isLoading && !data);
    const items: DistributionRow[] = React.useMemo(
        () =>
            (data?.distributions ?? []).map((row) => ({
                id: row.uuid,
                dateStr: row.distributedAt
                    ? new Date(row.distributedAt).toLocaleString("en-US", {
                          month: "short",
                          day: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit"
                      })
                    : "—",
                sigFull: row.transactionSignature,
                sigShort: row.transactionSignature
                    ? `${row.transactionSignature.slice(0, 6)}...${row.transactionSignature.slice(-6)}`
                    : "—",
                transactionUrl: `https://solscan.io/tx/${row.transactionSignature}${row.asset.network === "mainnet-beta" ? "" : `?cluster=${row.asset.network}`}`,
                amountStr: formatAmount(row.amountUi, row.asset),
                apyStr:
                    row.apyPct === null
                        ? "—"
                        : `${new BigDecimal(row.apyPct).round(2).toFixed(2)}%`
            })),
        [data]
    );
    const pageCount = Math.max(
        1,
        Math.ceil((data?.distributionCount ?? 0) / PAGE_SIZE)
    );
    // A refresh can remove the last page. Fetch the new final page in that case.
    React.useEffect(() => {
        if (data && page >= pageCount) setPage(pageCount - 1);
    }, [data, page, pageCount]);
    const safePage = page;
    const pageItems = items;

    // Before the first distribution the table has nothing to frame, so the
    // empty state replaces it rather than sitting in its empty row.
    const isEmpty = !isLoading && data?.distributionCount === 0;
    // With no record to show (none yet, or the call failed) the section is
    // the empty or error state and nothing else: no title, no summary
    // figures (all zeros and dashes), no History heading over an
    // illustration. After every hook, so the early return is safe.
    if (isError) return <DistributionHistoryError />;
    if (isEmpty) return <DistributionHistoryEmpty />;

    const totalDistributed = data
        ? data.totals
              .map((total) => formatAmount(total.amountUi, total.asset))
              .join(", ")
        : "—";
    const lastDistributionDate = data?.lastDistributionAt
        ? new Date(data.lastDistributionAt).toLocaleDateString("en-US", {
              month: "short",
              day: "2-digit",
              year: "numeric"
          })
        : "—";

    return (
        <div className="flex flex-col">
            {/* Matches SectionHeading's heading-to-content gap (`pb-2 md:pb-5`),
                so this title sits the same distance from its content as
                "APY History" does from the chart above it. The band's own
                top border supplies the 1px that heading's rule does. */}
            <div className="mb-2 md:mb-5">
                <Text variant="h1" render={<h1 />}>
                    Distributions
                </Text>
            </div>

            <div className="flex flex-col sm:flex-row border-t border-line-base">
                <div className="flex-1 pt-4 pb-5 sm:pt-5 sm:pb-6 border-b sm:border-b-0 border-line-base">
                    <MetricValue
                        label="Total Distributed"
                        value={totalDistributed}
                        valueVariant="number-lg"
                        size="lg"
                        labelClassName="text-foreground-muted"
                        loading={isLoading}
                    />
                </div>
                <div className="flex-1 pt-4 pb-5 sm:pt-5 sm:pb-6 border-b sm:border-b-0 border-line-base sm:flex sm:justify-center">
                    <MetricValue
                        label="Total Distributions"
                        value={data ? String(data.distributionCount) : "—"}
                        valueVariant="number-lg"
                        size="lg"
                        labelClassName="text-foreground-muted"
                        loading={isLoading}
                    />
                </div>
                <div className="flex-1 pt-4 pb-5 sm:pt-5 sm:pb-6">
                    <MetricValue
                        label="Last Distribution"
                        value={lastDistributionDate}
                        valueVariant="number-lg"
                        size="lg"
                        className="sm:items-end sm:text-right"
                        labelClassName="text-foreground-muted"
                        loading={isLoading}
                    />
                </div>
            </div>

            <div className="flex justify-between border-t border-line-base pt-2">
                <Text variant="small">Next Distribution</Text>
                <Text variant="small" className="text-foreground-muted">
                    Unavailable
                </Text>
            </div>
            <Text variant="h2" render={<h2 />} className="pt-7">
                History
            </Text>
            <div className="pt-5">
                <div className="flex flex-col gap-4">
                    <div className="overflow-x-auto">
                        {/* why: four columns, two of them full timestamps or
                                    signatures, need more room than a Tailwind width
                                    step provides before the scroller takes over. */}
                        <div className="min-w-[720px]">
                            <DataTable
                                columns={COLUMNS}
                                rows={pageItems}
                                rowKey={(row) => row.id}
                                loading={isLoading}
                                skeletonRows={PAGE_SIZE}
                                staggerKey={`distributions-${safePage}`}
                            />
                        </div>
                    </div>

                    {pageCount > 1 && (
                        <div className="flex items-center justify-end gap-2">
                            <ButtonIcon
                                variant="transparent"
                                size="md"
                                icon={<ChevronDoubleLeftIcon />}
                                aria-label="Previous Page"
                                disabled={safePage === 0}
                                onClick={() => {
                                    productEvent("stats:page_selected", {
                                        entry_point: "stats_distributions",
                                        direction: "previous",
                                        page: safePage - 1 + 1
                                    });
                                    setPage(safePage - 1);
                                }}
                            />
                            <Text
                                variant="number"
                                render={<span />}
                                aria-live="polite"
                            >
                                {safePage + 1}/{pageCount}
                            </Text>
                            <ButtonIcon
                                variant="transparent"
                                size="md"
                                icon={<ChevronDoubleRightIcon />}
                                aria-label="Next Page"
                                disabled={safePage >= pageCount - 1}
                                onClick={() => {
                                    productEvent("stats:page_selected", {
                                        entry_point: "stats_distributions",
                                        direction: "next",
                                        page: safePage + 1 + 1
                                    });
                                    setPage(safePage + 1);
                                }}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
